/**
 * MKS-410 — TanStack Query hooks for the helpdesk request record.
 *
 * Query-key convention, alongside MKS-339's `['helpdesk', ...]` subtree:
 *   ['helpdeskRequests', 'mine']            — the reporter's own list
 *   ['helpdeskRequests', 'queue', filters]  — the triage queue, keyed by the
 *                                             filter set so changing a filter
 *                                             is a new fetch and going back is
 *                                             a cache hit
 *   ['helpdeskRequests', 'types']           — the "New request" picker
 *
 * Every triage mutation invalidates the whole `['helpdeskRequests']` subtree
 * AND `['helpdesk']`: routing a request creates a ticket, which moves rows in
 * and out of MKS-339's inbox and changes its counts too, so a narrower
 * invalidation would leave a stale row on screen.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createHelpdeskRequest,
  fetchMyHelpdeskRequests,
  fetchHelpdeskRequestTypes,
  fetchAdminHelpdeskRequests,
  fetchHelpdeskRequestAttachments,
  uploadHelpdeskRequestAttachment,
  routeHelpdeskRequestToPerson,
  routeHelpdeskRequestToProject,
  setHelpdeskRequestStatus,
  type CreateHelpdeskRequestInput,
  type HelpdeskRequestFilters,
  type HelpdeskRequestRecord,
  type RouteToProjectInput,
  type SetRequestStatusInput,
} from '../services/api/helpdeskRequests';
import { attachmentResultFromError, type AttachmentResult } from './useCreateComment';

export type { AttachmentResult };

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useMyHelpdeskRequests(enabled = true) {
  return useQuery({
    queryKey: ['helpdeskRequests', 'mine'],
    queryFn: () => fetchMyHelpdeskRequests().then((r) => r.data),
    enabled,
  });
}

export function useHelpdeskRequestTypes(enabled = true) {
  return useQuery({
    queryKey: ['helpdeskRequests', 'types'],
    queryFn: () => fetchHelpdeskRequestTypes().then((r) => r.data),
    enabled,
  });
}

export function useAdminHelpdeskRequests(
  filters: HelpdeskRequestFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: ['helpdeskRequests', 'queue', filters],
    queryFn: () => fetchAdminHelpdeskRequests(filters).then((r) => r.data),
    enabled,
  });
}

export function useHelpdeskRequestAttachments(
  requestId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['helpdeskRequests', 'attachments', requestId],
    queryFn: () => fetchHelpdeskRequestAttachments(requestId!).then((r) => r.data),
    enabled: enabled && requestId != null,
  });
}

// ---------------------------------------------------------------------------
// Filing a request — create, THEN upload the staged files sequentially
// ---------------------------------------------------------------------------

export interface CreateHelpdeskRequestVariables {
  input: CreateHelpdeskRequestInput;
  /** Staged dropzone files — uploaded one by one AFTER create resolves. */
  files?: File[];
}

/**
 * The portal's `useCreatePortalRequest` shape, over the request record.
 *
 * `onCreated` fires ONLY once every upload has settled, with the per-file
 * outcomes, so the caller never sees a "created" signal mid-upload. The
 * caller's own `createdRequest` guard is what stops a retry after a partial
 * attachment failure from filing a SECOND request — the hook reports the
 * request it created on every settle, including the failing one, precisely so
 * that guard can be set.
 *
 * `isPending` deliberately covers the whole create-then-upload window rather
 * than just the create round trip.
 */
export function useCreateHelpdeskRequest(
  onCreated: (
    request: HelpdeskRequestRecord,
    attachmentResults: AttachmentResult[],
  ) => void,
) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  async function uploadStagedFiles(
    requestId: number,
    files: File[],
  ): Promise<AttachmentResult[]> {
    const results: AttachmentResult[] = [];
    for (const file of files) {
      try {
        await uploadHelpdeskRequestAttachment(requestId, file);
        results.push({ filename: file.name, status: 'ok' });
      } catch (err) {
        results.push(attachmentResultFromError(file.name, err));
      }
    }
    return results;
  }

  const base = useMutation({
    mutationFn: (input: CreateHelpdeskRequestInput) =>
      createHelpdeskRequest(input).then((r) => r.data),
  });

  return {
    ...base,
    isPending: base.isPending || uploading,
    mutate: ({ input, files }: CreateHelpdeskRequestVariables) => {
      base.mutate(input, {
        onSuccess: (request) => {
          function settle(results: AttachmentResult[]) {
            // "My requests" has a new row either way — a request whose
            // attachments failed still exists and must show up.
            qc.invalidateQueries({ queryKey: ['helpdeskRequests'] });
            onCreated(request, results);
          }
          if (!files || files.length === 0) {
            settle([]);
            return;
          }
          setUploading(true);
          void uploadStagedFiles(request.id, files).then((results) => {
            setUploading(false);
            settle(results);
          });
        },
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Triage writes
// ---------------------------------------------------------------------------

/**
 * Routing and the status flow both move a request between the queue's filters
 * and create or close a ticket, so both invalidate MKS-339's subtree too.
 */
function useTriageInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['helpdeskRequests'] });
    qc.invalidateQueries({ queryKey: ['helpdesk'] });
  };
}

export function useRouteRequestToPerson() {
  const invalidate = useTriageInvalidation();
  return useMutation({
    mutationFn: ({
      requestId,
      assigneeId,
    }: {
      requestId: number;
      assigneeId: number | null;
    }) => routeHelpdeskRequestToPerson(requestId, assigneeId).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useRouteRequestToProject() {
  const qc = useQueryClient();
  const invalidate = useTriageInvalidation();
  return useMutation({
    mutationFn: ({
      requestId,
      input,
    }: {
      requestId: number;
      input: RouteToProjectInput;
    }) => routeHelpdeskRequestToProject(requestId, input).then((r) => r.data),
    onSuccess: () => {
      invalidate();
      // Routing creates a real ticket in the project's Triage column, so a
      // board already on screen is now one card out of date.
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useSetRequestStatus() {
  const invalidate = useTriageInvalidation();
  return useMutation({
    mutationFn: ({
      requestId,
      input,
    }: {
      requestId: number;
      input: SetRequestStatusInput;
    }) => setHelpdeskRequestStatus(requestId, input).then((r) => r.data),
    onSuccess: invalidate,
  });
}
